import i18n from 'i18next';

export type Direction = 'ltr' | 'rtl';

export interface SupportedLanguage {
  code: string;
  /** Native name, shown in the switcher in its own script (never translated). */
  nativeLabel: string;
  dir: Direction;
}

// Order here is the order shown in the language switcher.
export const SUPPORTED_LANGUAGES: SupportedLanguage[] = [
  { code: 'fr', nativeLabel: 'Français', dir: 'ltr' },
  { code: 'en', nativeLabel: 'English', dir: 'ltr' },
  { code: 'zh', nativeLabel: '中文', dir: 'ltr' },
  { code: 'ja', nativeLabel: '日本語', dir: 'ltr' },
  { code: 'ar', nativeLabel: 'العربية', dir: 'rtl' },
];

export const SUPPORTED_LANGUAGE_CODES = SUPPORTED_LANGUAGES.map((l) => l.code);

export const FALLBACK_LANGUAGE = 'fr';

export const LANG_STORAGE_KEY = 'site-commande:lang';

export function isSupportedLanguage(code: string | null | undefined): code is string {
  return !!code && SUPPORTED_LANGUAGE_CODES.includes(code);
}

export function directionFor(code: string): Direction {
  return SUPPORTED_LANGUAGES.find((l) => l.code === code)?.dir ?? 'ltr';
}

function readStoredLanguage(): string | null {
  try {
    return localStorage.getItem(LANG_STORAGE_KEY);
  } catch {
    return null;
  }
}

function detectBrowserLanguage(): string | null {
  if (typeof navigator === 'undefined') return null;
  const candidates = [navigator.language, ...(navigator.languages ?? [])];
  for (const candidate of candidates) {
    if (!candidate) continue;
    const base = candidate.toLowerCase().split('-')[0];
    if (isSupportedLanguage(base)) return base;
  }
  return null;
}

// Precedence: explicit persisted choice > browser language > French fallback.
export function resolveInitialLanguage(): string {
  const stored = readStoredLanguage();
  if (isSupportedLanguage(stored)) return stored;
  return detectBrowserLanguage() ?? FALLBACK_LANGUAGE;
}

// Keeps <html lang/dir> in sync so the page reads correctly and RTL kicks in for Arabic.
export function applyDocumentLanguage(code: string): void {
  if (typeof document === 'undefined') return;
  const el = document.documentElement;
  el.lang = code;
  el.dir = directionFor(code);
}

export function changeAppLanguage(code: string): void {
  if (!isSupportedLanguage(code)) return;
  try {
    localStorage.setItem(LANG_STORAGE_KEY, code);
  } catch {
    // private mode / storage disabled — language still changes for the session
  }
  void i18n.changeLanguage(code);
  // Also applied by the languageChanged listener in i18n.ts, but call it here so
  // callers (and tests) observe the dir/lang update synchronously.
  applyDocumentLanguage(code);
}
